import {
    Avatar,
    Checkbox,
    CircularProgress,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableFooter,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Typography,
} from '@material-ui/core';
import { createStyles, withStyles, WithStyles } from '@material-ui/core/styles';
import { TableProps } from '@material-ui/core/Table';
import { TableCellProps } from '@material-ui/core/TableCell';
import { TableRowProps } from '@material-ui/core/TableRow';
import * as _ from 'lodash';
import { observer } from 'mobx-react';
import * as React from 'react';
import { DropResult } from 'react-beautiful-dnd';
import { ColumnSettingsDto } from 'src/generated_client';
import { DraggableTableHeaderCell } from '../DraggableTableHeaderCell';
import { DroppableTableHeaderRow } from '../DroppableTableHeaderRow';
import ESDataTableSettingsButton from '../ESDataTableSettingsButton';
import ESMenu from '../ESMenu';
import IDataTableManager from '../IDataTableManager';
import ITableOptions from '../ITableOptions';

const styles = createStyles({
    paperTable: {
        overflowX: 'auto',
        width: '100%',
    },
});
export interface IColumnSetting extends ColumnSettingsDto {
    isImage?: boolean;
}
export interface IMenuItem {
    disabled?: boolean;
    isConfirm?: boolean;
    icon?: string;
    onClick: (e: React.MouseEvent) => void;
    onMouseLeave?: (e: React.MouseEvent) => void;
    name: string;
    tooltip?: string;
}
export type IESDataTableProps = TableProps &
    WithStyles<typeof styles> & {
        dataTableManager: IDataTableManager<any>;
        disablePagination?: boolean;
        enableEdit?: boolean;
        enableReorderColumns?: boolean;
        enableToggleColumns?: boolean;
        onRowClick?: (event: React.MouseEvent, data: any, idx: number) => void;
        rowCellStyleFunction?: (data: any) => React.CSSProperties;
        rowsPerPage?: number;
    };
export interface IESDataTableState {
    isToggleColumnMenuOpen: boolean;
}
@observer
class ESDataTable extends React.Component<IESDataTableProps, IESDataTableState> {
    public state: IESDataTableState = {
        isToggleColumnMenuOpen: false,
    };

    public onSortLabelClick = (column: IColumnSetting) => () => {
        let newOrderBy: string | undefined;
        const { orderBy } = this.props.dataTableManager.tableOptions;
        if (!orderBy || orderBy.toLowerCase() !== column.sort!.toLowerCase()) {
            newOrderBy = column.fieldName + '+';
        } else if (
            orderBy.toLowerCase() === column.sort!.toLowerCase() &&
            orderBy[orderBy.length - 1] !== '-'
        ) {
            newOrderBy = column.fieldName + '-';
        }
        if (newOrderBy) {
            newOrderBy = this.upperCaseFirstCharacter(newOrderBy);
        }
        const columns = this.props.dataTableManager.columns.map((col) => {
            if (col.id !== column.id) {
                col.sort = '';
            } else {
                col.sort = newOrderBy;
            }
            return col;
        });
        this.onColumnsChanged(columns);
        this.updateTableOptions({ orderBy: newOrderBy, page: 1 });
    };

    public onRowClick = (rowOfData: any, idx: number) => (event: React.MouseEvent) => {
        if (this.props.onRowClick) {
            this.props.onRowClick(event, rowOfData, idx);
        }
    };

    public render() {
        const { classes, rowsPerPage, dataTableManager, disablePagination } = this.props;
        const { count, data } = dataTableManager;
        const footer = !disablePagination && (
            <TableFooter>
                <TableRow>
                    <TablePagination
                        count={count || 0}
                        onChangePage={this.onChangePage}
                        page={dataTableManager.tableOptions.page - 1}
                        rowsPerPage={rowsPerPage || 25}
                        rowsPerPageOptions={[]}
                    />
                </TableRow>
            </TableFooter>
        );
        return (
            <Paper className={classes.paperTable}>
                <Table>
                    {this.renderHeader()}
                    <TableBody>
                        {this.renderNoDataRow()}
                        {this.renderDataRows()}
                        {data && data.length > 10 && this.renderNoDataRow()}
                    </TableBody>
                    {footer}
                </Table>
            </Paper>
        );
    }

    public onChangePage = (_event: React.MouseEvent<HTMLButtonElement>, page: number) => {
        this.updateTableOptions({ page: page + 1 });
    };

    private updateTableOptions = (options: ITableOptions) => {
        const tableOptions = { ...this.props.dataTableManager.tableOptions, ...options };
        this.props.dataTableManager.updateTableOptions(tableOptions);
    };

    private reorderColumns(list: IColumnSetting[], startIndex: number, endIndex: number) {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
        return result;
    }

    private upperCaseFirstCharacter(thing = '') {
        return thing.charAt(0).toUpperCase() + thing.substr(1);
    }

    private isAnyMenuItems() {
        const data = this.props.dataTableManager.data;
        if (data) {
            return _.some(data, (row) => row.menuItems);
        }
        return false;
    }

    private isBoolean(data: any): boolean {
        return typeof data === 'boolean';
    }

    private renderDataRows(): React.ReactNode[] {
        const rows: React.ReactNode[] = [];
        const { columns, data } = this.props.dataTableManager;

        if (columns && data) {
            data.forEach((rowOfData, idx) => {
                const cells = this.renderCellsInRow(rowOfData);
                const row = (
                    <TableRow
                        data-cy="data-table-row"
                        key={rowOfData.id}
                        onClick={this.onRowClick(rowOfData, idx)}
                        selected={typeof rowOfData.isActive === 'boolean' && !rowOfData.isActive}
                        style={{
                            cursor: this.props.onRowClick ? 'pointer' : 'auto',
                        }}
                    >
                        {cells}
                    </TableRow>
                );
                rows.push(row);
            });
        }
        return rows;
    }

    private renderCellsInRow(rowOfData: any) {
        const { rowCellStyleFunction } = this.props;

        const cells: React.ReactNode[] = [];
        this.props.dataTableManager.columns.forEach((column, index) => {
            if (column.isVisible) {
                let cellValue = rowOfData[column.fieldName!];
                const cellStyles: React.CSSProperties = rowCellStyleFunction
                    ? { ...rowCellStyleFunction!(rowOfData) }
                    : {};
                if (this.isBoolean(cellValue)) {
                    cellValue = <Checkbox checked={cellValue} />;
                } else if (column.isImage) {
                    cellStyles.width = '40px';
                    cellValue = <Avatar src={rowOfData[column.fieldName!]} />;
                } else if (cellValue instanceof Date) {
                    cellValue = cellValue.toLocaleDateString(undefined, {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                    });
                } else if (column.type !== 'React.Component') {
                    if (!!cellValue) {
                        cellValue += '';
                    }
                }
                const colSpan = this.getColSpanForDataCell(index, rowOfData);
                cells.push(
                    <TableCell colSpan={colSpan} key={column.id} style={cellStyles}>
                        {cellValue}
                    </TableCell>
                );
            }
        });
        if (this.props.enableEdit && rowOfData.menuItems && rowOfData.menuItems.length > 0) {
            cells.push(
                <TableCell key="menuitem" align="right">
                    <ESMenu menuItems={rowOfData.menuItems} />
                </TableCell>
            );
        }
        return cells;
    }

    private onDragEnd = (result: DropResult) => {
        if (!result.destination) {
            return;
        }
        const columns = this.reorderColumns(
            this.props.dataTableManager.columns,
            result.source.index,
            result.destination.index
        );

        this.onColumnsChanged(columns);
    };

    private toggleVisibilityOfColumn = (column: IColumnSetting, index: number) => {
        const columns = [...this.props.dataTableManager.columns];
        columns[index].isVisible = !column.isVisible;
        this.onColumnsChanged(columns);
    };

    private getColSpanForDataCell(index: number, rowOfData: any) {
        let colSpan = 1;
        if (
            this.props.dataTableManager.columns.length - 1 === index &&
            (!rowOfData.menuItems || rowOfData.menuItems.length < 1) &&
            this.isAnyMenuItems()
        ) {
            colSpan = 2;
        }
        return colSpan;
    }

    private onColumnsChanged(columns: IColumnSetting[]) {
        this.props.dataTableManager.updateColumnSettings(columns);
    }

    private renderHeader(): React.ReactNode {
        const headerCells: React.ReactNode[] = [];
        const { enableReorderColumns, enableToggleColumns, dataTableManager } = this.props;
        const { columns, tableOptions } = dataTableManager;
        if (columns) {
            columns.forEach((column, index) => {
                if (column.isVisible) {
                    const last = columns.length - 1 === index;
                    let cellValue = column.columnName;
                    if (column.isImage) {
                        cellValue = '';
                    }
                    const cellProps: TableCellProps = {};
                    if (enableReorderColumns) {
                        cellProps.component = DraggableTableHeaderCell(column.id!, index);
                    }
                    headerCells.push(
                        <TableCell
                            {...cellProps}
                            key={column.id}
                            sortDirection={this.getSortDirection(column)}
                            colSpan={!enableToggleColumns && this.isAnyMenuItems() && last ? 2 : 1}
                        >
                            <TableSortLabel
                                active={
                                    !!tableOptions.orderBy && tableOptions.orderBy === column.sort
                                }
                                direction={this.getSortDirection(column)}
                                onClick={this.onSortLabelClick(column)}
                            >
                                {cellValue}
                            </TableSortLabel>
                        </TableCell>
                    );
                }
            });
            if (enableToggleColumns) {
                headerCells.push(
                    <TableCell key="options" align="right">
                        <ESDataTableSettingsButton
                            columns={columns}
                            toggleVisibilityOfColumn={this.toggleVisibilityOfColumn}
                        />
                    </TableCell>
                );
            }
        }
        const rowProps: TableRowProps = {};
        if (enableReorderColumns) {
            rowProps.component = DroppableTableHeaderRow(this.onDragEnd);
        }
        return (
            <TableHead>
                <TableRow {...rowProps}>{headerCells}</TableRow>
            </TableHead>
        );
    }

    private getSortDirection = (column: IColumnSetting) => {
        const { orderBy } = this.props.dataTableManager.tableOptions;
        if (!orderBy) {
            return;
        } else if (orderBy === column.sort) {
            return orderBy[orderBy.length - 1] === '-' ? 'desc' : 'asc';
        }
        return;
    };

    private renderNoDataRow(): React.ReactNode {
        const { data, isLoading } = this.props.dataTableManager;
        if (isLoading || (data && data.length === 0)) {
            const progressCircle = isLoading && <CircularProgress size={50} />;
            const noDataText = !isLoading && data && data.length === 0 && (
                <Typography variant="display1">No Data</Typography>
            );
            const additionalCols = this.isAnyMenuItems() ? 1 : 0;
            const numberOfColumns = this.props.dataTableManager.columns.length;
            const colSpan = numberOfColumns + additionalCols;
            return (
                <TableRow>
                    <TableCell key="progress" colSpan={colSpan}>
                        <Grid container justify="center" alignItems="center">
                            {progressCircle}
                            {noDataText}
                        </Grid>
                    </TableCell>
                </TableRow>
            );
        }
        return null;
    }
}

export default withStyles(styles)(ESDataTable);



// WEBPACK FOOTER //
// ./src/common/ESDataTable/ESDataTable.tsx